// Banner Service for API communication
interface BannerData {
  id: number;
  type: string;
  title: string;
  content: string;
  icon: string | null;
  link: string | null;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface BannerResponse {
  success: boolean;
  data: BannerData | null;
  message?: string;
}

interface BannerUpdateRequest {
  content?: string;
  is_visible?: boolean;
}

interface BannerUpdateResponse {
  success: boolean;
  message: string;
  data: BannerData;
}

class BannerService {
  private baseUrl: string;
  private lastRequestTime: number = 0;
  private minRequestInterval: number = 1000; // Minimum 1 second between requests

  constructor() {
    // Use mock API for development, production API when database is connected
    // With proxy configuration, we can use relative URLs
    this.baseUrl = process.env.REACT_APP_API_URL || '/api';
  }

  /**
   * Get current banner data with rate limiting protection
   */
  async getBanner(): Promise<BannerResponse> {
    try {
      // Check rate limiting
      const now = Date.now();
      if (now - this.lastRequestTime < this.minRequestInterval) {
        console.log('Banner request throttled to prevent rate limiting');
        await new Promise(resolve => setTimeout(resolve, this.minRequestInterval - (now - this.lastRequestTime)));
      }
      
      this.lastRequestTime = Date.now();
      const response = await fetch(`${this.baseUrl}/static-data/banner`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching banner:', error);
      return {
        success: false,
        data: null,
        message: 'Failed to fetch banner data'
      };
    }
  }

  /**
   * Update banner content and/or visibility
   * Creates new banner if none exists
   */
  async updateBanner(content?: string, isVisible?: boolean): Promise<BannerUpdateResponse> {
    try {
      const body: BannerUpdateRequest = {};
      
      if (content !== undefined) {
        body.content = content;
      }
      
      if (isVisible !== undefined) {
        body.is_visible = isVisible;
      }

      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${this.baseUrl}/static-data/banner`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      // Log the response for debugging
      console.log('Banner API Response:', data);
      
      return data;
    } catch (error) {
      console.error('Error updating banner:', error);
      return {
        success: false,
        message: 'Failed to update banner',
        data: {} as BannerData
      };
    }
  }

  /**
   * Update only banner content
   */
  async updateBannerContent(content: string): Promise<BannerUpdateResponse> {
    return this.updateBanner(content, undefined);
  }

  /**
   * Update only banner visibility
   */
  async updateBannerVisibility(isVisible: boolean): Promise<BannerUpdateResponse> {
    return this.updateBanner(undefined, isVisible);
  }

  /**
   * Update both content and visibility
   */
  async updateBannerFull(content: string, isVisible: boolean): Promise<BannerUpdateResponse> {
    return this.updateBanner(content, isVisible);
  }
}

// Export singleton instance
const bannerService = new BannerService();
export default bannerService;

// Export types for use in components
export type { BannerData, BannerResponse, BannerUpdateResponse };
